import { useState } from "react";
import { TrashIcon, Edit3Icon, RotateCcw } from "lucide-react";
import { useNavigate } from "react-router-dom";

import BreadCrumb from "../../../Components/BreadCrumb";
import TableListing from "../../../Components/TableListing";
import { ToastMessage } from "../../../Components/Utils";
import { Button } from "../../../Components/Buttons";
import { ROUTE_PATHS } from "../../../constants/routeConstants";
import { handleError } from "../../../stores/components/handleError";
import {
  useFetchUsersQuery,
  useFetchTrashedUsersQuery,
  useDeleteUserMutation,
  useDestroyUserMutation,
  useRestoreUserMutation,
  useBulkDeleteUsersMutation,
  useBulkDestroyUsersMutation,
  useBulkRestoreUsersMutation,
} from "../../../stores/api/users.api";
import payloadHelper from "../../../stores/api/helpers/payload.helper";

const EMPTY_PAGINATION = {
  data: [],
  current_page: 1,
  total_pages: 1,
  total_items: 0,
  current_items: 0,
  limit: 10,
};

const UsersList = () => {
  const navigate = useNavigate();
  const [trashed, setTrashed] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const queryParams = payloadHelper.forList({
    page,
    limit,
    search,
    sortBy: sortConfig.key,
    sortOrder: sortConfig.direction,
  });

  const activeQuery = useFetchUsersQuery(queryParams, { skip: trashed });
  const trashedQuery = useFetchTrashedUsersQuery(queryParams, { skip: !trashed });
  const { data: users = EMPTY_PAGINATION, isFetching } = trashed ? trashedQuery : activeQuery;

  const [deleteUser] = useDeleteUserMutation();
  const [destroyUser] = useDestroyUserMutation();
  const [restoreUser] = useRestoreUserMutation();
  const [bulkDeleteUsers] = useBulkDeleteUsersMutation();
  const [bulkDestroyUsers] = useBulkDestroyUsersMutation();
  const [bulkRestoreUsers] = useBulkRestoreUsersMutation();

  const resetPage = () => setPage(1);

  const handleSingle = (row, type = "delete") => {
    const verb = type === "restore" ? "restore" : "delete";
    ToastMessage({
      type: "warning",
      message: `You sure you wanna ${verb} this User?`,
      title: `User ${verb === "delete" ? "Delete" : "Restore"}..!`,
      toastType: "confirm",
      onYes: async () => {
        try {
          if (type === "restore") {
            await restoreUser(row.id).unwrap();
          } else if (type === "destroy") {
            await destroyUser(row.id).unwrap();
          } else {
            await deleteUser(row.id).unwrap();
          }
          ToastMessage({
            type: "success",
            title: `User ${type === "restore" ? "Restored" : "Deleted"}!`,
            message: `${type === "restore" ? "Restored" : "Deleted"} User: ${row.name}`,
          });
        } catch (err) {
          handleError(err, "Failed to update user.");
        }
      },
    });
  };

  const handleMultiple = (selectedItems, type = "delete") => {
    const verb = type === "restore" ? "restore" : "delete";
    ToastMessage({
      type: "warning",
      message: `You sure you wanna ${verb} these Users?`,
      title: `Users ${verb === "delete" ? "Delete" : "Restore"}..!`,
      toastType: "confirm",
      onYes: async () => {
        try {
          const body = payloadHelper.forBulk(selectedItems);
          if (type === "restore") {
            await bulkRestoreUsers(body).unwrap();
          } else if (type === "destroy") {
            await bulkDestroyUsers(body).unwrap();
          } else {
            await bulkDeleteUsers(body).unwrap();
          }
          ToastMessage({
            type: "success",
            title: `${type === "restore" ? "Restored" : "Deleted"} selected users`,
            message: `${selectedItems.length} user(s) ${type === "restore" ? "restored" : "deleted"} successfully.`,
          });
        } catch (err) {
          handleError(err, "Failed bulk action.");
        }
      },
    });
  };

  const handleTrashedChange = (value) => {
    setTrashed(value);
    resetPage();
  };

  const headers = [
    { key: "image", label: "Image", sortable: false, type: "image" },
    { key: "name", label: "Name", sortable: true },
    { key: "email", label: "Email", sortable: true },
    { key: "role.name", label: "Role", sortable: false, type: "badge" },
  ];

  const renderActions = (row) => (
    <div className="flex gap-2">
      {!trashed ? (
        <Button
          onClick={() => navigate(ROUTE_PATHS.CREATE_USER, { state: { user_details: row } })}
          title="Edit"
          icon={Edit3Icon}
          className="text-green-600 hover:bg-green-100 hover:text-green-700 dark:hover:bg-green-500/10"
        />
      ) : (
        <Button
          onClick={() => handleSingle(row, "restore")}
          title="Restore"
          icon={RotateCcw}
          className="text-green-600 hover:bg-green-100 hover:text-green-700 dark:hover:bg-green-500/10"
        />
      )}
      <Button
        onClick={() => handleSingle(row, trashed ? "destroy" : "delete")}
        title="Delete"
        icon={TrashIcon}
        className="text-red-600 hover:bg-red-100 hover:text-red-700 dark:hover:bg-red-500/10"
      />
    </div>
  );

  return (
    <>
      <BreadCrumb title="Users" />
      <TableListing
        headers={headers}
        paginationData={users}
        loading={isFetching}
        createButton
        createLink={ROUTE_PATHS.CREATE_USER}
        actions={renderActions}
        handleBulk={handleMultiple}
        search={search}
        setSearch={setSearch}
        trashed={trashed}
        setTrashed={setTrashed}
        onTrashedChange={handleTrashedChange}
        onPageChange={setPage}
        onRowsPerPageChange={(value) => {
          setLimit(value);
          resetPage();
        }}
        onSearch={(value) => {
          setSearch(value);
          resetPage();
        }}
        onSortChange={(sort) => {
          setSortConfig(sort);
          resetPage();
        }}
      />
    </>
  );
};

export default UsersList;
